(() => {
  const payload = window.CCM_JUDGEME_KO;
  const approvedReviews = payload?.reviews;

  if (!payload || !approvedReviews || typeof approvedReviews !== 'object') return;

  const MAX_READINESS_ATTEMPTS = 40;
  const READINESS_INTERVAL_MS = 250;
  const REVIEW_CARD_SELECTOR = '.jm-review-item[review-uuid]';
  const TITLE_SELECTOR = '.jm-review-content__title';
  const BODY_SELECTOR =
    '.jm-review-content__body .jdgm-review-content__body-content p';
  const BODY_CONTENT_SELECTOR =
    '.jm-review-content__body .jdgm-review-content__body-content';
  const BODY_FALLBACK_SELECTOR = '.jm-review-content__body';
  const TOGGLE_SELECTOR = '.jdgm-translate-button';
  const WIDGET_SELECTOR = '.jdgm-review-widget[data-product-id]';
  const TRANSLATED_LABEL = '원문 보기';
  const ORIGINAL_LABEL = '한국어로 보기';
  const heldReviewIds = new Set(payload.heldReviewIds || []);
  const cardStates = new WeakMap();
  let readinessAttempts = 0;
  let observer = null;

  const getWidget = () => document.querySelector(WIDGET_SELECTOR);

  const getReviewUuid = (card) => card.getAttribute('review-uuid') || '';

  const getCardElements = (card) => ({
    title: card.querySelector(TITLE_SELECTOR),
    body:
      card.querySelector(BODY_SELECTOR) ||
      card.querySelector(BODY_CONTENT_SELECTOR) ||
      card.querySelector(BODY_FALLBACK_SELECTOR),
    toggle: card.querySelector(TOGGLE_SELECTOR)
  });

  const setText = (element, value) => {
    if (!element || element.textContent === value) return;

    if (
      element.childNodes.length === 1 &&
      element.firstChild?.nodeType === 3
    ) {
      element.firstChild.data = value;
      return;
    }

    element.textContent = value;
  };

  const renderCard = (card, state, mode) => {
    const elements = getCardElements(card);
    if (!elements.body || !elements.toggle) return false;

    if (mode === 'translated') {
      setText(elements.title, state.translation.title);
      setText(elements.body, state.translation.body);
      setText(elements.toggle, TRANSLATED_LABEL);
    } else {
      setText(elements.title, state.originalTitle);
      setText(elements.body, state.originalBody);
      setText(elements.toggle, ORIGINAL_LABEL);
    }

    state.mode = mode;
    card.dataset.pdpApprovedTranslationState = mode;
    return true;
  };

  const hydrateCard = (card) => {
    const reviewUuid = getReviewUuid(card);

    if (heldReviewIds.has(reviewUuid)) {
      const toggle = card.querySelector(TOGGLE_SELECTOR);
      if (toggle) toggle.hidden = true;
      card.dataset.pdpApprovedTranslationHeld = 'true';
      return false;
    }

    const translation = approvedReviews[reviewUuid];
    if (!translation) return false;

    const elements = getCardElements(card);
    if (!elements.body || !elements.toggle) return false;

    let state = cardStates.get(card);
    if (!state || state.reviewUuid !== reviewUuid) {
      state = {
        reviewUuid,
        translation,
        originalTitle:
          typeof translation.originalTitle === 'string'
            ? translation.originalTitle
            : elements.title?.textContent || '',
        originalBody:
          typeof translation.originalBody === 'string'
            ? translation.originalBody
            : elements.body.textContent || '',
        mode: 'translated'
      };
      cardStates.set(card, state);
    }

    return renderCard(card, state, state.mode);
  };

  const processNode = (node) => {
    if (node?.nodeType !== 1) return;

    const cards = new Set();
    if (node.matches(REVIEW_CARD_SELECTOR)) cards.add(node);
    node
      .querySelectorAll?.(REVIEW_CARD_SELECTOR)
      .forEach((card) => cards.add(card));

    const containingCard = node.closest?.(REVIEW_CARD_SELECTOR);
    if (containingCard && !cardStates.has(containingCard)) {
      cards.add(containingCard);
    }

    cards.forEach((card) => hydrateCard(card));
  };

  const updateLoadedCount = (widget) => {
    const loadedCount = Array.from(
      widget.querySelectorAll(REVIEW_CARD_SELECTOR)
    ).filter((card) => cardStates.has(card)).length;
    widget.dataset.pdpApprovedTranslationsLoaded = String(loadedCount);
  };

  const bindWidget = (widget) => {
    if (widget.dataset.pdpApprovedTranslationHooks === 'true') return true;

    widget.dataset.pdpApprovedTranslationHooks = 'true';
    widget.dataset.pdpApprovedTranslations = String(payload.reviewCount || 0);
    widget.dataset.pdpApprovedTranslationDataset = payload.datasetSha256 || '';

    widget.querySelectorAll(REVIEW_CARD_SELECTOR).forEach(hydrateCard);
    updateLoadedCount(widget);

    widget.addEventListener(
      'click',
      (event) => {
        const toggle = event.target.closest?.(TOGGLE_SELECTOR);
        const card = toggle?.closest?.(REVIEW_CARD_SELECTOR);
        if (!toggle || !card) return;

        const reviewUuid = getReviewUuid(card);
        if (heldReviewIds.has(reviewUuid)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        const state = cardStates.get(card);
        if (!state) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        renderCard(
          card,
          state,
          state.mode === 'translated' ? 'original' : 'translated'
        );
      },
      true
    );

    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === 'attributes' &&
            mutation.target.matches?.(REVIEW_CARD_SELECTOR)
          ) {
            hydrateCard(mutation.target);
            return;
          }

          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) processNode(node);
          });

          const containingCard =
            mutation.target.parentElement?.closest?.(REVIEW_CARD_SELECTOR) ||
            mutation.target.closest?.(REVIEW_CARD_SELECTOR);
          if (containingCard && cardStates.has(containingCard)) {
            hydrateCard(containingCard);
          }
        });
        updateLoadedCount(widget);
      });
      observer.observe(widget, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['review-uuid']
      });
    }

    return true;
  };

  const waitForJudgeMe = () => {
    const widget = getWidget();
    if (widget && bindWidget(widget)) return;
    readinessAttempts += 1;
    if (readinessAttempts < MAX_READINESS_ATTEMPTS) {
      window.setTimeout(waitForJudgeMe, READINESS_INTERVAL_MS);
    }
  };

  const initialize = () => {
    waitForJudgeMe();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
